import { type Transition, animate, motion, useMotionValue } from "motion/react"
import { useEffect, useRef } from "react"

import { cn } from "../../lib/utils"

const DEFAULT_TRANSITION: Transition = {
  duration: 0.3,
  ease: [0.645, 0.045, 0.355, 1], // ease-in-out-cubic
}

interface AnimatedCardProps {
  className?: string
  contentClassName?: string
  transition?: Transition
  children?: React.ReactNode
}

function AnimatedCard({ className, contentClassName, transition, children }: AnimatedCardProps) {
  const contentRef = useRef<HTMLDivElement>(null)
  const height = useMotionValue<number | "auto">("auto")
  const isInitial = useRef(true)
  const transitionRef = useRef(transition)
  transitionRef.current = transition

  // Sync DOM content height for smooth resize
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    let active: { stop: () => void } | undefined

    const observer = new ResizeObserver(() => {
      if (!contentRef.current) return
      const h = contentRef.current.offsetHeight
      if (isInitial.current) {
        isInitial.current = false
        height.set(h)
      } else {
        active?.stop()
        active = animate(height, h, transitionRef.current ?? DEFAULT_TRANSITION)
      }
    })
    observer.observe(el)
    return () => {
      observer.disconnect()
      active?.stop()
    }
  }, [height])

  return (
    <motion.div
      data-slot="card"
      style={{ height }}
      className={cn(
        "bg-card text-card-foreground rounded-xl border shadow-sm overflow-hidden",
        className,
      )}
    >
      <div ref={contentRef} className={cn("flex flex-col gap-6 py-6", contentClassName)}>
        {children}
      </div>
    </motion.div>
  )
}

export { AnimatedCard }
export type { AnimatedCardProps }
